/**
 * ProvidersListPage — paginated, searchable, filterable table of all providers (Feature D).
 *
 * Sub-stories implemented here:
 *   D1 — Paginated table with status badge; loads on mount.
 *   D2 — ~300 ms debounced search by name or short code; resets to page 1.
 *   D3 — Status and Provider Type filter dropdowns; append query params to API call.
 *   D4 — Row click navigates to the Provider Profile page.
 *   D5 — "Add New Provider" button navigates to the creation form.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import * as Select from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { PageBanner, Button, AlertMessage, SkeletonTable } from "@/components/reusable";
import { ProviderStatusBadge } from "@/components/providers/ProviderStatusBadge";
import { fetchProviders } from "@/services/providersApi";
import { useTelemetry } from "@/hooks/useTelemetry";
import { useProviderPermissions } from "@/hooks/useProviderPermissions";
import { cn } from "@/lib/utils";
import { PROVIDER_TYPES, PROVIDER_TYPE_LABELS, PROVIDER_STATUSES } from "@/types/providers";
import type { ProviderOut, ProviderStatus, ProviderType } from "@/types/providers";

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Formats an ISO date string as a short locale date, or returns "—" for null. */
function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ── Filter Select ──────────────────────────────────────────────────────────────

interface FilterSelectProps {
  value: string;
  onValueChange: (v: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
  className?: string;
}

/**
 * Compact single-select filter dropdown used for Status and Type filters.
 * Selecting the placeholder value clears the filter.
 */
function FilterSelect({
  value,
  onValueChange,
  placeholder,
  options,
  className,
}: FilterSelectProps) {
  return (
    <Select.Root value={value || undefined} onValueChange={onValueChange}>
      <Select.Trigger
        className={cn(
          "flex h-9 min-w-[140px] items-center justify-between gap-2 rounded-md border px-3 text-sm outline-none transition",
          "border-[var(--color-border)] bg-white hover:border-[var(--color-action-primary)]",
          "focus:border-[var(--color-action-primary)]",
          "data-[placeholder]:text-[var(--color-text-tertiary)]",
          value
            ? "border-[var(--color-action-primary)] text-[var(--color-text-primary)]"
            : "text-[var(--color-text-tertiary)]",
          className
        )}
      >
        <Select.Value placeholder={placeholder} />
        <Select.Icon>
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          className="z-50 overflow-hidden rounded-lg border border-[var(--color-border)] bg-white shadow-lg animate-in fade-in-0 zoom-in-95"
          position="popper"
          sideOffset={4}
        >
          <Select.Viewport className="p-1">
            {/* Clear option */}
            <Select.Item
              value="_all"
              onSelect={() => onValueChange("")}
              className="relative flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-sm text-[var(--color-text-tertiary)] italic outline-none data-[highlighted]:bg-[var(--color-bg-secondary)]"
            >
              <Select.ItemText>{placeholder}</Select.ItemText>
            </Select.Item>
            {options.map((opt) => (
              <Select.Item
                key={opt.value}
                value={opt.value}
                className="relative flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-sm outline-none data-[highlighted]:bg-[var(--color-bg-secondary)]"
              >
                <Select.ItemText>{opt.label}</Select.ItemText>
                <Select.ItemIndicator className="ml-auto">
                  <Check className="h-4 w-4 text-[var(--color-brand-primary)]" />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function ProvidersListPage() {
  const navigate = useNavigate();
  const { track, trackError } = useTelemetry();
  const { canCreateProvider } = useProviderPermissions();

  // ── State ──────────────────────────────────────────────────────────────────
  const [providers, setProviders] = useState<ProviderOut[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter / search state
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Debounce timer ref — cleared on each keystroke and fires after SEARCH_DEBOUNCE_MS
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Derived ────────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const offset = (currentPage - 1) * PAGE_SIZE;
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);

  // ── Debounce search input → reset to page 1 when query changes ─────────────
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchInput]);

  // Reset to page 1 when filters change
  const handleStatusChange = useCallback((v: string) => {
    setStatusFilter(v === "_all" ? "" : v);
    setCurrentPage(1);
  }, []);

  const handleTypeChange = useCallback((v: string) => {
    setTypeFilter(v === "_all" ? "" : v);
    setCurrentPage(1);
  }, []);

  // ── Fetch providers when any query param changes ────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchProviders({
      search: debouncedSearch || undefined,
      status: statusFilter || undefined,
      providerType: typeFilter || undefined,
      offset,
      limit: PAGE_SIZE,
    })
      .then((res) => {
        if (cancelled) return;
        setProviders(res.items);
        setTotal(res.total);
        track("providers_list_loaded", {
          total: res.total,
          search: debouncedSearch,
          statusFilter,
          typeFilter,
          page: currentPage,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Failed to load providers.";
        setError(message);
        trackError(err instanceof Error ? err : new Error(message), {
          context: "providers_list_load",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, statusFilter, typeFilter, offset, currentPage, track, trackError]);

  // ── Navigation ──────────────────────────────────────────────────────────────

  /** D4 — Open a provider's detail page on row click. */
  const handleRowClick = useCallback(
    (provider: ProviderOut) => {
      track("provider_row_clicked", { providerId: provider.provider_id });
      navigate(`/provider-management/providers/${provider.provider_id}`);
    },
    [navigate, track]
  );

  /** D5 — Navigate to the Add New Provider form. */
  const handleAddNew = useCallback(() => {
    navigate("/provider-management/providers/new");
  }, [navigate]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const statusOptions = PROVIDER_STATUSES.map((s) => ({
    value: s,
    label: s.charAt(0).toUpperCase() + s.slice(1),
  }));

  const typeOptions = PROVIDER_TYPES.map((t) => ({
    value: t,
    label: PROVIDER_TYPE_LABELS[t as ProviderType],
  }));

  return (
    <div className="animate-fade-in">
      <PageBanner title="Providers" subtitle="Manage data provider registrations" />

      {/* Toolbar: search + filters + Add button */}
      <div className="mb-6 flex flex-wrap items-center gap-3 animate-fade-in-up stagger-2">
        {/* D2 — Debounced search */}
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-text-tertiary)]" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or short code…"
            className="h-9 w-full rounded-md border border-[var(--color-border)] bg-white pl-9 pr-3 text-sm outline-none transition focus:border-[var(--color-action-primary)]"
          />
        </div>

        {/* D3 — Status filter */}
        <FilterSelect
          value={statusFilter}
          onValueChange={handleStatusChange}
          placeholder="All statuses"
          options={statusOptions}
        />

        {/* D3 — Provider type filter */}
        <FilterSelect
          value={typeFilter}
          onValueChange={handleTypeChange}
          placeholder="All types"
          options={typeOptions}
        />

        {/* D5 — Add New Provider (App.ADMIN only) */}
        {canCreateProvider && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="ml-auto"
            onClick={handleAddNew}
          >
            <Plus className="h-4 w-4" />
            Add New Provider
          </Button>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-6">
          <AlertMessage variant="error" message={error} />
        </div>
      )}

      {/* Loading skeleton */}
      {loading ? (
        <SkeletonTable rows={8} columns={7} />
      ) : providers.length === 0 ? (
        /* Empty state */
        <div className="flex flex-col items-center justify-center rounded-xl border border-[var(--color-border-light)] bg-white py-20 text-center animate-fade-in-up">
          <p className="text-base font-medium text-[var(--color-text-primary)]">
            {debouncedSearch || statusFilter || typeFilter
              ? "No providers match your filters."
              : "No providers registered yet."}
          </p>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {!(debouncedSearch || statusFilter || typeFilter) && (
              <>
                Get started by{" "}
                <button
                  type="button"
                  onClick={handleAddNew}
                  className="text-[var(--color-brand-primary)] underline-offset-2 hover:underline"
                >
                  adding your first provider
                </button>
                .
              </>
            )}
          </p>
        </div>
      ) : (
        /* D1 — Providers table */
        <div className="rounded-xl border border-[var(--color-border-light)] overflow-hidden animate-fade-in-up stagger-2">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg-secondary)]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Provider Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Short Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Schemas
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Last Submission
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Created
                </th>
              </tr>
            </thead>
            <tbody>
              {providers.map((provider, idx) => (
                <tr
                  key={provider.provider_id}
                  onClick={() => handleRowClick(provider)}
                  className={cn(
                    "cursor-pointer transition-colors hover:bg-[var(--color-bg-secondary)]",
                    idx > 0 && "border-t border-[var(--color-border-light)]"
                  )}
                >
                  <td className="px-4 py-3 font-medium text-[var(--color-text-primary)]">
                    {provider.provider_name}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--color-text-secondary)]">
                    {provider.provider_short_code}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {PROVIDER_TYPE_LABELS[provider.provider_type] ?? provider.provider_type}
                  </td>
                  <td className="px-4 py-3">
                    <ProviderStatusBadge status={provider.provider_status as ProviderStatus} />
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {provider.assigned_schemas.length === 0 ? (
                      <span className="text-[var(--color-text-tertiary)] italic">None</span>
                    ) : (
                      <span
                        title={provider.assigned_schemas
                          .map((s) => s.schema_name ?? s.schema_id)
                          .join(", ")}
                      >
                        {provider.assigned_schemas.length === 1
                          ? (provider.assigned_schemas[0]?.schema_name ??
                            provider.assigned_schemas[0]?.schema_id ??
                            "—")
                          : `${provider.assigned_schemas.length} schemas`}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {formatDate(provider.last_submission_at)}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {formatDate(provider.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination controls */}
      {!loading && total > 0 && (
        <div className="mt-4 flex items-center justify-between animate-fade-in-up stagger-3">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Showing {rangeStart}–{rangeEnd} of {total} provider{total !== 1 ? "s" : ""}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-[var(--color-border)] transition hover:bg-[var(--color-bg-secondary)] disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {/* Page number pills — show up to 5 centred around current page */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => Math.abs(p - currentPage) <= 2)
              .map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setCurrentPage(p)}
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-md border text-sm transition",
                    p === currentPage
                      ? "border-[var(--color-action-primary)] bg-[var(--color-action-primary)] text-white"
                      : "border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)]"
                  )}
                >
                  {p}
                </button>
              ))}

            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-[var(--color-border)] transition hover:bg-[var(--color-bg-secondary)] disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
